import { Reveal } from "./Reveal";

const FOOTBALL_IMG =
  "https://images.unsplash.com/photo-1706675780107-7c43cc487928?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxmb290YmFsbCUyMHN0YWRpdW0lMjBsaWdodHMlMjBkYXJrfGVufDB8fHx8MTc5Mjk5NzgxNHww&ixlib=rb-4.1.0&q=85";
const RUNNING_IMG =
  "https://images.unsplash.com/photo-1759674804375-3d0c038a0a6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxtYXJhdGhvbiUyMHJ1bm5pbmclMjBkYXJrJTIwbW9vZHl8ZW58MHx8fHwxNzkyOTk3ODE0fDA&ixlib=rb-4.1.0&q=85";
const OBSTACLE_IMG =
  "https://images.unsplash.com/photo-1776938023428-60ddf175a2d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxtdWQlMjBvYnN0YWNsZSUyMGNvdXJzZSUyMHJhY2UlMjBkYXJrfGVufDB8fHx8MTc5Mjk5NzgxNHww&ixlib=rb-4.1.0&q=85";

const SectionLabel = ({ children }) => (
  <p className="text-xs uppercase tracking-[0.25em] text-[#0EA5E9] font-mono-alt">{children}</p>
);

export const BeyondCode = () => (
  <section id="beyond" data-testid="beyond-section" className="py-24 md:py-32 lg:py-40 border-t border-white/10 bg-[#0A0A0A]">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.25em] text-[#71717A] font-mono-alt">Beyond the Code</p>
        <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05] font-medium text-[#F9F9F9] mt-4 max-w-3xl">
          Building isn’t the only way I push my limits.
        </h2>
        <p className="text-base md:text-lg text-[#A1A1AA] mt-8 max-w-2xl leading-relaxed">
          Outside technology and entrepreneurship, sport has been another way for me to challenge
          myself. From competitive football to endurance events and obstacle challenges, I enjoy
          stepping into environments that demand consistency, resilience, and commitment.
        </p>
      </Reveal>

      {/* A. FOOTBALL — cinematic full-width */}
      <Reveal className="mt-24 md:mt-32">
        <div data-testid="beyond-football" className="relative overflow-hidden rounded-sm border border-white/10 group">
          <img
            src={FOOTBALL_IMG}
            alt="Football stadium under floodlights at night"
            className="w-full h-[420px] md:h-[600px] object-cover opacity-70 transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/30 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6 md:p-12 lg:p-16">
            <SectionLabel>Football</SectionLabel>
            <h3 className="font-heading text-3xl md:text-5xl tracking-tight font-medium text-[#F9F9F9] mt-4">
              Competition. Teamwork. Commitment.
            </h3>
            <p className="text-sm md:text-base text-[#A1A1AA] mt-4 max-w-xl leading-relaxed">
              Football has been a significant part of my journey, giving me opportunities to compete
              in organized tournaments and experience the demands of team sport.
            </p>
            <div className="flex flex-wrap gap-10 md:gap-16 mt-8">
              <div data-testid="football-stat-state">
                <p className="font-heading text-5xl md:text-7xl font-medium text-[#F9F9F9] leading-none">
                  3<span className="text-[#0EA5E9]">×</span>
                </p>
                <p className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mt-3">State-Level Tournament Appearances</p>
              </div>
              <div data-testid="football-stat-zonal">
                <p className="font-heading text-5xl md:text-7xl font-medium text-[#F9F9F9] leading-none">
                  4<span className="text-[#0EA5E9]">×</span>
                </p>
                <p className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mt-3">Zonal Tournament Appearances</p>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      {/* B. ENDURANCE RUNNING + C. DEVIL'S CIRCUIT — asymmetric pair */}
      <div className="mt-16 md:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <Reveal className="lg:col-span-7">
          <div data-testid="beyond-running">
            <div className="relative overflow-hidden rounded-sm border border-white/10 group">
              <img
                src={RUNNING_IMG}
                alt="Runners in motion during a distance race"
                className="w-full h-[320px] md:h-[440px] object-cover opacity-75 transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#080808]/90 via-transparent to-transparent" />
              <p className="absolute bottom-5 left-6 text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] font-mono-alt">
                Showing up. Putting in the work. Going the distance.
              </p>
            </div>
            <div className="mt-8">
              <SectionLabel>Endurance Running</SectionLabel>
              <h3 className="font-heading text-2xl md:text-4xl tracking-tight font-medium text-[#F9F9F9] mt-4">
                One step at a time.
              </h3>
              <p className="text-sm md:text-base text-[#A1A1AA] mt-4 max-w-lg leading-relaxed">
                Running has given me another way to challenge myself, develop consistency, and
                experience the satisfaction of working toward a difficult physical goal.
              </p>
              <div className="flex gap-3 mt-6">
                <span className="text-[11px] uppercase tracking-[0.12em] text-[#F9F9F9] border border-white/15 rounded-full px-4 py-1.5">Half-Marathons</span>
                <span className="text-[11px] uppercase tracking-[0.12em] text-[#F9F9F9] border border-white/15 rounded-full px-4 py-1.5">Marathons</span>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-5 lg:mt-24" delay={0.1}>
          <div data-testid="beyond-devils-circuit">
            <div className="relative overflow-hidden rounded-sm border border-white/10 group">
              <img
                src={OBSTACLE_IMG}
                alt="Athlete climbing a wall on an obstacle course"
                className="w-full h-[280px] md:h-[360px] object-cover opacity-75 transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#080808]/80 via-transparent to-transparent" />
            </div>
            <div className="mt-8">
              <SectionLabel>Devil’s Circuit</SectionLabel>
              <h3 className="font-heading text-2xl md:text-4xl tracking-tight font-medium text-[#F9F9F9] mt-4">
                Built to be challenged.
              </h3>
              <p className="text-sm md:text-base text-[#A1A1AA] mt-4 leading-relaxed">
                Obstacle-course challenges offer a different test of physical preparation,
                endurance, and mental resilience.
              </p>
              <p className="text-sm md:text-base text-[#71717A] mt-3 leading-relaxed">
                I’ve taken on Devil’s Circuit — an obstacle-course event that combines physical
                challenges with endurance.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
